import { PeoplePickerUser } from "../../../components/components/fileViewer/pdfViewer/editorComponents/SignatureBoxesPanel";
import { SignatureBox } from "../../../interfaces/file/fileSignature/SignatureBox";
import {
  CreateSignatureRequestPayload,
  SignatureRequestResponse,
} from "../../../interfaces/file/fileSignature/SignatureCreationRequests";
import {
    AllSignatureRequestResDto,
  SignatureRequest,
  SignatureRequestQueryReq,
} from "../../../interfaces/file/fileSignature/SignatureRequest";
import { ApiResponse } from "../../../interfaces/genericResponse/GenericResponse";
import {
  PagedResult,
  SignatureRequestDto,
} from "../../../interfaces/signature/SignatureRequestDto";
import { SignatureRequestQueryDto } from "../../../interfaces/signature/SignatureRequestQueryDto";

// Creates a signature request and returns the created record
export async function createSignatureRequest(
  payload: CreateSignatureRequestPayload,
  authToken: string
): Promise<SignatureRequestResponse | null> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/generate-request`;

  console.log("Request Payload", payload);
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error || "Failed to create signature request");
    }

    return response.json();
  } catch (error) {
    console.error("Error creating signature request", error);
    throw error;
  }
}

export async function fetchSignatureRequestByToken(
  token: string
): Promise<SignatureRequest | null> {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/sign/public/${token}`,
      {
        method: "GET",
      }
    );
    if (!res.ok) {
      console.log(res);
      return null;
    }

    console.log(`${process.env.REACT_APP_API_BASE_URL}/sign/public/${token}`);
    const response = await res.json();
    console.log(response);
    return response;
  } catch (err) {
    console.log(err);
    return null;
  }
}

export async function fillSignatureBox(
  token: string,
  boxId: string,
  signatureValue: string
): Promise<boolean> {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/sign/public/${token}/boxes/${boxId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signatureValue }),
      }
    );
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Submits a completed signature request.
 * This endpoint should only be called once all signature boxes have been signed.
 * @param token The signature request token.
 * @returns True if submission was successful; false otherwise.
 */
export async function submitSignatureRequest(
  token: string
): Promise<{ finalizedPdfUrl: string; signingLogUrl: string } | null> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/submit/${token}`;
    // We assume no additional body data is needed.
    const response = await fetch(apiUrl, {
      method: "PUT",
    });
    if (response.status === 200) {
      return response.json();
    } else {
      const responseValue = await response.text();
      console.log(responseValue);
    }
    return null;
  } catch (error) {
    console.error("Error submitting signature request", error);
    return null;
  }
}

export async function searchEmails(
  query: string,
  token: string
): Promise<PeoplePickerUser[]> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL
      }/sign/search?query=${encodeURIComponent(query)}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      const result = await response.json();
      // Assuming the API returns: [{ email: "test@example.com" }, ...]
      // return result.map((item: { email: string }) => item.email);
      return await result;
    } else {
      const errorText = await response.text();
      console.log("Search failed:", errorText);
    }

    return [];
  } catch (error) {
    console.error("Error searching emails:", error);
    return [];
  }
}
export async function getPendingSignatureRequests(
  authToken: string,
  queryDto: SignatureRequestQueryDto
): Promise<PagedResult<SignatureRequestDto> | null> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/SignaturePendingRequest`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(queryDto),
    });

    if (!response.ok) {
      console.error(
        "Failed to fetch signature requests",
        await response.json()
      );
      return null;
    }

    const result: PagedResult<SignatureRequestDto> = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching signature requests", error);
    return null;
  }
}

export async function resendSignatureRequest(
  authToken: string,
  requestId: string
): Promise<boolean> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/resendSignature-request?fileId=${requestId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response) {
      console.error("Failed to resend signature request");
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error resending signature request", error);
    return false;
  }
}

export async function getSignatureBoxes(
  authToken: string,
  fileId: string,
  ownerId: string
): Promise<SignatureBox[] | null> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/signature-boxes?fileId=${fileId}&ownerId=${ownerId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      console.error("Failed to fetch signature boxes");
      return null;
    }

    const data = await response.json();
    return data as SignatureBox[];
  } catch (error) {
    console.error("Error fetching signature boxes", error);
    return null;
  }
}

export async function submitSignature(
  authToken: string,
  fileId: string,
  ownerId: string
): Promise<any | null> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/submit?fileId=${fileId}&ownerId=${ownerId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      console.error("Failed to submit signature request");
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error submitting signature request", error);
    return null;
  }
}

export async function submitSignatureCancelReq(
  authToken: string,
  fileId: string,
  ownerId: string,
  status: string = "completed" // optional param
): Promise<ApiResponse<string>> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/update-status`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileId,
        ownerId,
        status,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

export async function getSignatureStatus(
  reqToken: string
): Promise<ApiResponse<string> | null> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/getSignatureStatus/${reqToken}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return null;
    }

    const result: ApiResponse<string> = await response.json();
    return result;
  } catch (error) {
    console.error("Error fetching signature status", error);
    return null;
  }
}

export async function cancelSignatureRequestById(
  authToken: string,
  signBoxId: string
): Promise<{ success: boolean; message?: string }> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/cancel-by-request-id/${signBoxId}`;
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    const responseText = await response.text();

    if (response.status === 200) {
      return { success: true, message: responseText };
    } else if (response.status === 404) {
      return { success: false, message: `Not Found: ${responseText}` };
    } else {
      return { success: false, message: `Server Error: ${responseText}` };
    }
  } catch (error: any) {
    console.error("Error cancelling signature request", error);
    return { success: false, message: "Network or unexpected error occurred." };
  }
}

export async function deleteSignatureBoxById(
  authToken: string,
  boxId: string
): Promise<{ success: boolean; message?: string }> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/box/${boxId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    const data = await response.json(); // Assuming your API returns the ApiResponse object

    if (response.ok) {
      return { success: true, message: data.message };
    } else {
      return { success: false, message: data.message || "Failed to delete box" };
    }
  } catch (error) {
    console.error("Error in deleteSignatureBoxById service:", error);
    return { success: false, message: "Network or unexpected error occurred." };
  }
}

export async function deleteSignatureRequest(
  authToken: string,
  boxId: string
): Promise<{ success: boolean; message?: string }> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/box/${boxId}`;

  try {
    const response = await fetch(apiUrl, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || `Failed to delete signature request: ${response.statusText}`
      };
    }

    const data = await response.json().catch(() => ({ message: "Deleted successfully" }));
    return { success: true, message: data.message || "Signature request deleted successfully" };
  } catch (error) {
    console.error("Error in deleteSignatureRequest service:", error);
    return { success: false, message: "Network or unexpected error occurred." };
  }
}

export async function getSignatureRequestsForFile(
  authToken: string,
  fileId: string
): Promise<SignatureRequest[] | null> {
  try {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/file/${fileId}`;
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (response.status === 200) {
      const responseVal = await response.json();
      console.log(responseVal);
      return responseVal;
    } else {
      const responseValue = await response.text();
      console.log(responseValue);
    }
    return null;
  } catch (error) {
    console.error("Error fetching signature requests", error);
    return null;
  }
}

// services/documentService.ts

export async function getAllSignatureRequests(
  token: string,
  payload: SignatureRequestQueryReq
): Promise<{ items: AllSignatureRequestResDto[]; totalCount: number } | null> {
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/sign/AllSignatureRequest`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.error("Failed to fetch signature requests");
      return null;
    }

    const data = await response.json();

    console.log("Signature Requests", data);
    return {
      items: data.items,
      totalCount: data.totalCount,
    };
  } catch (error) {
    console.error("Error fetching signature requests:", error);
    return null;
  }
}

export async function getSignatureInboxRequests(
  idToken: string,
  userId: string,
  pageNumber: number,
  pageSize: number,
  searchTerm: string,
  sortColumn?: string,
  sortDirection?: string
): Promise<{ items: any[]; totalCount: number }> {
  const queryParams = new URLSearchParams({
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
    ...(sortColumn && { sortColumn }),
    ...(sortDirection && { sortDirection }),
  });

  if (searchTerm) {
    queryParams.append("searchTerm", searchTerm);
  }
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL
    }/sign/signature-inbox-requests/${userId}?${queryParams.toString()}`;

  const response = await fetch(apiUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw {
      status: response.status,
      message: "Failed to fetch signature inbox requests",
      details: errorBody,
    };
  }

  const result = await response.json();

  // Handle both response structures: direct {items, totalCount} or wrapped {data: {items, totalCount}}
  const items = result.items || result.data?.items || [];
  const totalCount = result.totalCount || result.data?.totalCount || 0;

  if (!Array.isArray(items)) {
    console.error("Expected items to be an array, got:", items);
    return { items: [], totalCount: 0 };
  }

  return {
    items: items,
    totalCount: totalCount,
  };
}

function generatePagination(currentPage: number, totalPages: number) {
  const buttons: (number | string)[] = [];

  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) buttons.push(i);
  } else {
    if (currentPage > 2) buttons.push(1);
    if (currentPage > 3) buttons.push("...");
    for (
      let i = Math.max(1, currentPage - 1);
      i <= Math.min(totalPages, currentPage + 1);
      i++
    ) {
      buttons.push(i);
    }
    if (currentPage < totalPages - 2) buttons.push("...");
    if (currentPage < totalPages - 1) buttons.push(totalPages);
  }

  return buttons;
}
